// Parser HTML na wiadomości (dymki czatu)

export interface ParsedMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  turnId?: string;
}

/**
 * Parsuje HTML z backendu na listę wiadomości do wyświetlenia w dymkach.
 * Backend zwraca HTML z divami zawierającymi klasy i turn-id.
 * 
 * @param htmlContent - zawartość HTML z backendu
 * @returns tablica sparsowanych wiadomości
 */
export const parseMessagesToBubbles = (htmlContent: string): ParsedMessage[] => {
  if (!htmlContent || htmlContent.trim() === '') {
    return [];
  }

  const messages: ParsedMessage[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  
  // Szukamy divów z klasami message-user lub message-assistant
  // lub divów zawierających turn-id
  const messageElements = doc.querySelectorAll('div[class*="message"], div[data-turn-id], .user-message, .assistant-message, .ai-message');
  
  if (messageElements.length === 0) {
    // Fallback: jeśli nie ma specjalnych klas, dzielimy po paragrafach lub divach
    // Zakładamy, że każdy główny element to osobna wiadomość
    const topLevelElements = Array.from(doc.body.children);
    
    topLevelElements.forEach((element, index) => {
      const content = element.innerHTML.trim();
      if (content) {
        // Próba wykrycia typu na podstawie treści lub położenia
        // (nieparzyste indeksy = użytkownik, parzyste = asystent - to tylko założenie)
        const type: 'user' | 'assistant' = index % 2 === 0 ? 'user' : 'assistant';
        
        messages.push({
          id: `msg-${index}`,
          type,
          content,
          turnId: extractTurnIdFromElement(element)
        });
      }
    });
  } else {
    // Przetwarzamy znalezione elementy wiadomości
    messageElements.forEach((element, index) => {
      const content = element.innerHTML.trim();
      const classList = Array.from(element.classList);
      
      // Określ typ wiadomości na podstawie klasy
      let type: 'user' | 'assistant' = 'assistant';
      if (classList.some(cls => cls.includes('user'))) {
        type = 'user';
      }
      
      if (content) {
        messages.push({
          id: `msg-${index}`,
          type,
          content,
          turnId: extractTurnIdFromElement(element)
        });
      }
    });
  }

  return messages;
};

/**
 * Wydobywa turn-id z elementu lub jego komentarzy
 */
const extractTurnIdFromElement = (element: Element): string | undefined => {
  // Sprawdź atrybut data-turn-id
  const dataTurnId = element.getAttribute('data-turn-id');
  if (dataTurnId) return dataTurnId;
  
  // Szukaj w komentarzach HTML
  const html = element.outerHTML;
  const commentMatch = html.match(/<!--\s*turn-id:([a-f0-9-]+)\s*-->/i);
  if (commentMatch) return commentMatch[1];
  
  return undefined;
};

/**
 * Upraszcza parsowanie - dzieli HTML na wiadomości użytkownika i asystenta.
 * Backend Orion zwraca HTML ze strukturą:
 * <!-- turn-id:xxx -->
 * <p class='user-message'>tekst użytkownika</p>
 * <p><details class='agent-message'>...</details></p> (pomijamy, chyba że includeDetails=true)
 * <p>odpowiedź asystenta...</p>
 * <table>...</table>
 * 
 * @param htmlContent - zawartość HTML z backendu
 * @param includeDetails - czy zachować szczegóły developerskie (Tool Call Request/Result, statystyki)
 */
export const simpleParseMessages = (htmlContent: string, includeDetails = false): ParsedMessage[] => {
  if (!htmlContent || htmlContent.trim() === '') {
    return [];
  }

  const messages: ParsedMessage[] = [];
  
  // 1. Usuń agent-message details (debug info) - ale tylko te, które NIE zawierają operacji
  // W trybie developerskim (includeDetails=true) zachowujemy wszystkie szczegóły
  // Najpierw usuń typing indicator
  let cleanedHtml = htmlContent.replace(/<div\s+class=['"]typing-indicator['"][\s\S]*?<\/div>/gi, '');
  
  // 2. Podziel HTML na części po turn-id PRZED wyodrębnianiem operacji
  // Dzięki temu będziemy mogli przypisać każdą operację do właściwego turn-id
  const parts = cleanedHtml.split(/<!--\s*turn-id:([a-f0-9-]+)\s*-->/gi);
  
  // Funkcja pomocnicza do wyodrębniania operacji z HTML
  const extractOperationFromHtml = (html: string): { operationHtml: string; foundDetails: string[] } => {
    const operationMatches: string[] = [];
    const foundDetails: string[] = [];
    const agentMessagePattern = /<details\s+class=['"]agent-message['"][\s\S]*?<\/details>/gi;
    let match;
    
    while ((match = agentMessagePattern.exec(html)) !== null) {
      const detailsContent = match[0];
      // Sprawdź czy zawiera informacje o operacji (completed lub executing)
      const isOperationCompleted = detailsContent.includes('Operation completed:');
      const isOperationExecuting = detailsContent.includes('Operation executing:') || 
                                   detailsContent.includes('Executing operation:') ||
                                   (detailsContent.includes('Operation:') && detailsContent.includes('chat-status') && !isOperationCompleted);
      
      if (isOperationCompleted || isOperationExecuting) {
        // Zachowaj strukturę <details> z <summary> dla rozwijanej zawartości
        const detailsInnerMatch = detailsContent.match(/<details[^>]*>([\s\S]*?)<\/details>/i);
        if (detailsInnerMatch) {
          const detailsInner = detailsInnerMatch[1];
          
          // Wyodrębnij summary i resztę
          const summaryMatch = detailsInner.match(/<summary>([\s\S]*?)<\/summary>/i);
          const restContent = detailsInner.replace(/<summary>[\s\S]*?<\/summary>/i, '').trim();
          
          // Sprawdź status operacji na podstawie klasy chat-status
          const statusMatch = detailsInner.match(/<span\s+class=['"]chat-status\s+(\w+)['"]/i);
          const status = statusMatch ? statusMatch[1] : null;
          const isExecuting = isOperationExecuting || status === 'executing' || status === 'loading' || status === 'pending';
          
          // Stwórz HTML dla wiadomości o operacji z <details> i <summary> dla rozwijania
          let operationHtml = '';
          let summaryText = '';
          let spinnerHtml = '';
          
          if (isExecuting) {
            // Dla operacji w trakcie wykonywania - dodaj spinner i tekst "Executing operation"
            spinnerHtml = `<span style="display: inline-block; width: 16px; height: 16px; border: 2px solid #e0e0e0; border-top: 2px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite; margin-right: 8px; vertical-align: middle; flex-shrink: 0;"></span>`;
            
            // Wyodrębnij nazwę operacji z summary (jeśli istnieje)
            if (summaryMatch) {
              const operationNameMatch = summaryMatch[1].match(/Operation(?:\s+(?:executing|completed))?:\s*(.+)/i);
              const operationName = operationNameMatch ? operationNameMatch[1].replace(/<span[^>]*><\/span>/gi, '').trim() : '';
              summaryText = operationName ? `Executing operation: ${operationName}` : 'Executing operation...';
            } else {
              summaryText = 'Executing operation...';
            }
            
            // Dla executing - nie pokazuj zawartości (Parameters, Result), tylko spinner
            operationHtml = `<style>
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
            <div style="margin: 10px 0; padding: 12px; border: 1px solid #e0e0e0; border-radius: 6px; background-color: #f9f9f9;">
              <div style="font-weight: 600; display: flex; align-items: center; color: #555;">
                ${spinnerHtml}
                <span>${summaryText}</span>
              </div>
            </div>`;
          } else {
            // Dla completed - normalny format z <details>
            if (summaryMatch) {
              // Usuń <span class='chat-status success'></span> z summary jeśli istnieje, ale zachowaj tekst
              summaryText = summaryMatch[1].replace(/<span[^>]*><\/span>\s*/gi, '').trim();
              
              // Stwórz <details> z <summary> - domyślnie zwinięte (bez atrybutu open)
              operationHtml = `<details style="margin: 10px 0; padding: 10px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;">
                <summary style="font-weight: bold; cursor: pointer; user-select: none;">${summaryText}</summary>
                <div style="font-size: 0.7em; margin-top: 8px; padding-top: 8px; border-top: 1px solid #eee;">
                  ${restContent}
                </div>
              </details>`;
            } else {
              // Jeśli nie ma summary, użyj całej zawartości
              operationHtml = `<details style="margin: 10px 0; padding: 10px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;">
                <summary style="font-weight: bold; cursor: pointer; user-select: none;">Operation completed</summary>
                <div style="font-size: 0.7em; margin-top: 8px; padding-top: 8px; border-top: 1px solid #eee;">
                  ${restContent || detailsInner}
                </div>
              </details>`;
            }
          }
          
          if (operationHtml.trim()) {
            operationMatches.push(operationHtml.trim());
            foundDetails.push(detailsContent);
          }
        }
      }
    }
    
    return { operationHtml: operationMatches.join(''), foundDetails };
  };
  
  // Mapowanie turn-id do operacji
  const operationByTurnId: Map<string, string[]> = new Map();
  
  // Przejdź przez części i wyodrębnij operacje dla każdej części osobno
  for (let i = 0; i < parts.length; i += 2) {
    const contentBlock = parts[i];
    const turnId = i > 0 ? parts[i - 1] : undefined;
    
    if (contentBlock && contentBlock.trim() && turnId) {
      const { operationHtml, foundDetails: blockFoundDetails } = extractOperationFromHtml(contentBlock);
      
      if (operationHtml) {
        // Usuń znalezione details z tego bloku
        let blockHtml = contentBlock;
        blockFoundDetails.forEach((details) => {
          blockHtml = blockHtml.replace(details, '');
        });
        parts[i] = blockHtml; // Zaktualizuj część bez operacji
        
        // Przypisz operację do odpowiedniego turn-id
        if (!operationByTurnId.has(turnId)) {
          operationByTurnId.set(turnId, []);
        }
        operationByTurnId.get(turnId)?.push(operationHtml);
      }
    }
  }
  
  // Zbuduj cleanedHtml z zaktualizowanych części, zachowując komentarze turn-id
  // parts[0] to tekst przed pierwszym turn-id
  // parts[1] to turn-id
  // parts[2] to treść do następnego turn-id
  // itd.
  cleanedHtml = '';
  for (let i = 0; i < parts.length; i += 2) {
    const contentBlock = parts[i];
    const turnId = i > 0 ? parts[i - 1] : undefined;
    
    if (i > 0 && turnId) {
      // Dodaj komentarz turn-id przed treścią
      cleanedHtml += `<!-- turn-id:${turnId} -->`;
    }
    cleanedHtml += contentBlock || '';
  }
  
  // Usuń pozostałe agent-message details (jeśli jakieś zostały)
  // W trybie developerskim (includeDetails=true) zachowujemy wszystkie szczegóły
  if (!includeDetails) {
    cleanedHtml = cleanedHtml.replace(/<details\s+class=['"]agent-message['"][\s\S]*?<\/details>/gi, '');
  }
  
  // 3. Podziel cleanedHtml na części po turn-id (ponownie, bo mogliśmy zmienić części)
  const finalParts = cleanedHtml.split(/<!--\s*turn-id:([a-f0-9-]+)\s*-->/gi);

  for (let i = 0; i < finalParts.length; i += 2) {
    const contentBlock = finalParts[i];
    const turnId = i > 0 ? finalParts[i - 1] : undefined;

    if (contentBlock && contentBlock.trim()) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(contentBlock.trim(), 'text/html');

      // 1. Szukaj wiadomości użytkownika
      const userMessageElements = doc.querySelectorAll('.user-message');
      if (userMessageElements.length > 0) {
        userMessageElements.forEach((element, idx) => {
          messages.push({
            id: `${turnId || 'user'}-${i}-${idx}`,
            type: 'user',
            content: element.outerHTML.trim(), // Zachowaj cały HTML paragrafu
            turnId: turnId,
          });
          element.remove(); // Usuń przetworzony element
        });
      }
      
      // 2. Wyodrębnij informacje o załącznikach z paragrafów minor-agent-message
      const attachmentInfoElements = doc.querySelectorAll('p.minor-agent-message');
      attachmentInfoElements.forEach((element) => {
        const elementText = element.textContent || '';
        
        if (elementText.includes('Reading attachment content:')) {
          // Wyodrębnij tekst z elementu
          const textContent = elementText.trim();
          // Stwórz ładnie sformatowany HTML dla informacji o załączniku
          const styledAttachmentInfo = `<div style="margin: 10px 0; padding: 12px; border: 1px solid #e0e0e0; border-radius: 6px; background-color: #f0f8ff; color: #555; font-size: 0.9em;">
            ${textContent}
          </div>`;
          
          messages.push({
            id: `attachment-info-${turnId}-${messages.length}`,
            type: 'assistant',
            content: styledAttachmentInfo,
            turnId: turnId,
          });
          
          element.remove(); // Usuń przetworzony element
        }
      });

      // 3. Dodaj informacje o operacji PO informacjach o załącznikach, ale PRZED odpowiedzią asystenta
      if (turnId && operationByTurnId.has(turnId)) {
        const operations = operationByTurnId.get(turnId);
        if (operations && operations.length > 0) {
          operations.forEach((opHtml, idx) => {
            if (opHtml && opHtml.trim()) {
              messages.push({
                id: `operation-${turnId}-${idx}`,
                type: 'assistant',
                content: opHtml.trim(),
                turnId: turnId,
              });
            }
          });
        }
      }

      // W trybie developerskim - rozdziel szczegóły developerskie na dwie kategorie:
      // 1. Tool Call Request/Result - przed odpowiedzią asystenta
      // 2. Statystyki użycia - po odpowiedzi asystenta
      const developerDetailsBefore: string[] = [];
      const developerDetailsAfter: string[] = [];
      
      if (includeDetails) {
        const developerDetails = doc.querySelectorAll('details.agent-message');
        developerDetails.forEach((detail) => {
          const detailContent = detail.outerHTML.trim();
          // Sprawdź czy to nie jest operacja (operacje są już przetworzone)
          const isOperation = detailContent.includes('Operation completed:') || 
                             detailContent.includes('Operation executing:') ||
                             detailContent.includes('Executing operation:');
          
          if (!isOperation && detailContent) {
            // Formatuj szczegóły developerskie z mniejszą czcionką
            const formattedDetail = `<div style="font-size: 0.75em; margin: 10px 0; padding: 10px; border: 1px solid #ddd; border-radius: 4px; background-color: #f9f9f9;">
              ${detailContent}
            </div>`;
            
            // Sprawdź czy to statystyki użycia (Log: Statystyki użycia)
            const isUsageStats = detailContent.includes('Log: Statystyki użycia') || 
                                detailContent.includes('Statystyki użycia (cała runda)') ||
                                detailContent.includes('Szacunkowy koszt');
            
            if (isUsageStats) {
              // Statystyki użycia - na końcu, po odpowiedzi asystenta
              developerDetailsAfter.push(formattedDetail);
            } else {
              // Tool Call Request/Result - przed odpowiedzią asystenta
              developerDetailsBefore.push(formattedDetail);
            }
            
            // Usuń z dokumentu, aby nie duplikować w assistantContent
            detail.remove();
          }
        });
      }

      // Dodaj Tool Call Request/Result PRZED odpowiedzią asystenta
      if (includeDetails && developerDetailsBefore.length > 0) {
        developerDetailsBefore.forEach((formattedDetail, idx) => {
          messages.push({
            id: `dev-details-before-${turnId || 'unknown'}-${i}-${idx}`,
            type: 'assistant',
            content: formattedDetail,
            turnId: turnId,
          });
        });
      }

      // Reszta to wiadomości asystenta (dane, tabele, itp.)
      const assistantContent = doc.body.innerHTML.trim();
      if (assistantContent) {
        messages.push({
          id: turnId || `assistant-${i}`,
          type: 'assistant',
          content: assistantContent,
          turnId: turnId,
        });
      }

      // Na końcu dodaj statystyki użycia - PO odpowiedzi asystenta
      if (includeDetails && developerDetailsAfter.length > 0) {
        developerDetailsAfter.forEach((formattedDetail, idx) => {
          messages.push({
            id: `dev-details-after-${turnId || 'unknown'}-${i}-${idx}`,
            type: 'assistant',
            content: formattedDetail,
            turnId: turnId,
          });
        });
      }
    }
  }

  return messages;
};

